import React from "react";
import { colors } from "../../constants/theme";

export interface UploadNotification {
  id: string;
  filename: string;
  status: "uploading" | "success" | "error";
  progress?: number; // For future use with real progress
}

interface UploadNotificationProps {
  notifications: UploadNotification[];
  onClear: (id: string) => void;
  onClearAll: () => void;
}

export const UploadNotificationArea: React.FC<UploadNotificationProps> = ({
  notifications,
  onClear,
  onClearAll,
}) => {
  if (notifications.length === 0) return null;

  const successfulUploads = notifications.filter((n) => n.status === "success");
  const hasSuccessful = successfulUploads.length > 0;

  return (
    <div
      style={{
        position: "fixed",
        bottom: 20,
        right: 20,
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        gap: 8,
        maxWidth: 400,
      }}
    >
      {notifications.map((notification) => (
        <div
          key={notification.id}
          style={{
            background: colors.card,
            borderRadius: 8,
            padding: 16,
            boxShadow: colors.shadow,
            border: `1px solid ${colors.border}`,
            minWidth: 300,
            animation: "slideIn 0.3s ease-out",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 12,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: colors.text,
                  marginBottom: 4,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {notification.filename}
              </div>
              {notification.status === "uploading" && (
                <div>
                  <div
                    style={{
                      height: 4,
                      background: colors.bg,
                      borderRadius: 2,
                      overflow: "hidden",
                      marginTop: 8,
                      position: "relative",
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        background: colors.accent,
                        width: "40%",
                        position: "absolute",
                        animation: "progress 1.5s ease-in-out infinite",
                      }}
                    />
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: colors.muted,
                      marginTop: 6,
                    }}
                  >
                    Uploading...
                  </div>
                </div>
              )}
              {notification.status === "success" && (
                <div
                  style={{
                    fontSize: 12,
                    color: "#2e7d32",
                    marginTop: 4,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: "#2e7d32",
                      display: "inline-block",
                    }}
                  />
                  Upload complete
                </div>
              )}
              {notification.status === "error" && (
                <div
                  style={{
                    fontSize: 12,
                    color: "#d32f2f",
                    marginTop: 4,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: "#d32f2f",
                      display: "inline-block",
                    }}
                  />
                  Upload failed
                </div>
              )}
            </div>
            <button
              onClick={() => onClear(notification.id)}
              style={{
                background: "transparent",
                border: "none",
                color: colors.muted,
                cursor: "pointer",
                padding: 4,
                fontSize: 18,
                lineHeight: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              ×
            </button>
          </div>
        </div>
      ))}

      {hasSuccessful && (
        <button
          onClick={onClearAll}
          style={{
            background: colors.card,
            border: `1px solid ${colors.border}`,
            borderRadius: 8,
            padding: "8px 12px",
            fontSize: 12,
            color: colors.text,
            cursor: "pointer",
            fontWeight: 600,
            alignSelf: "flex-end",
          }}
        >
          Clear successful uploads
        </button>
      )}
    </div>
  );
};

// Add CSS animations
const style = document.createElement("style");
style.textContent = `
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  
  @keyframes progress {
    0% {
      left: -40%;
    }
    100% {
      left: 100%;
    }
  }
`;
if (!document.head.querySelector('style[data-upload-notifications]')) {
  style.setAttribute('data-upload-notifications', 'true');
  document.head.appendChild(style);
}

